import React, {useContext, useEffect, useState} from 'react';
import {Page} from '../../presenters/pages/index';
import {AppContext, AppContextSpec, useAsyncEffect} from '../../App';
import {strings} from '../../i18n/strings';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import {grey} from '@material-ui/core/colors';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import {useOktaAuth} from '../../../../okta-react';
import {getData} from "../../domain/repository/DiagnosticRepository";
import {HeadCell} from "../../../../graphics/tables/data/HeadCell";
import {DatasetStructure} from "../../domain/indexes/DatasetIndexes";
import {DynamicDatatable} from "../../../../graphics/tables/DynamicDatatable";
import {Order} from "../../../../graphics/tables/operations/sort";
import {TableRowData} from "../../../../graphics/tables/data/TableRowData";
import {DiagnosticStructure} from "../../domain/indexes/DiagnosticIndexes";
import {Compellable} from "../../domain/indexes/Indexes";
import {exportDataToExcel} from "../../domain/repository/DiagnosticRepository";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootFilter: {
            display: 'flex',
            flexDirection: 'row',
            '& > *': {
                marginBottom: '1em',
                marginRight: '1em'
            }
        },
        filterPresentation: {
            textDecoration: 'underline',
            fontWeight: 'bold',
            textAlign: 'left'
        },
        cfoStockPresentation: {
            textAlign: 'left'
        },
        waitingSelection: {
            color: grey[600],
            borderRadius: '4px',
            borderColor: grey[600],
            border: 'solid 1px'
        },
        saveAction: {
            margin: '15px 0 21px 0',
            width: '150px',
            marginLeft: 'auto'
        },
        filterItem: {
            display: 'flex',
            marginBottom: '1em',
            marginRight: '1em',
        },
        buttonContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        marginTop20: {
            marginTop: '20px',
            color: grey[600],
            borderRadius: '4px',
            borderColor: grey[600],
            border: 'solid 1px'
        },
        positiveStock: {
            color: grey[500],
        },
        negativeStock: {},
    })
);

export type Record = DiagnosticStructure.RecordOf<Compellable>;

interface RecordRepresentation extends Record, TableRowData {
};

const wordingDiagnostic = strings.page.diagnostic;

export const DiagnosticPage = () => {

    const {authState} = useOktaAuth();
    const classes = useStyles();
    const appContext = useContext<AppContextSpec>(AppContext);
    const [pagination, setPagination] = useState<DatasetStructure.Pagination>();
    const [headCellsConfig, setHeadCellsConfig] = useState<HeadCell<any>[]>([]);
    const wordingsDatatable = strings.datatable;
    const [limit, setLimit] = useState(20);
    const [page, setPage] = useState(1);
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<string>('');
    const [allRows, setAllRows] = useState<RecordRepresentation[]>([]);
    const [rowData, setRowData] = useState<RecordRepresentation[]>([]);
    const [tableKey, setTableKey] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    useAsyncEffect(async () => {
            await getAndRefreshRecords();
        }, [page, limit]
    );

    useEffect(() => {
        setPage(1);
        setLimit(20);
    }, []);

    const getAndRefreshRecords = async () => {
        const initialPage = 1;
        const initialLimit = 20;

        setPage(initialPage);  // Set page to 1
        setLimit(initialLimit); // Set limit to default
        resetTable();

        try {
            const response: DiagnosticStructure.DynamicResponse<any> = await getData<any>(authState!);

            const headCells: HeadCell<any>[] = response.headers.map((field, index) => {
                return {
                    id: field,
                    label: field,
                    displayName: field.toUpperCase().replace('_', ' '),
                    type: "",
                    required: true,
                    information: "",
                    position: 1,
                    visible: true,
                    editable: false,
                    massEditable: false,
                    reference: "",
                    referenceColumnLabel: "",
                    disablePadding: false,
                    searchValue: "",
                };
            });
            const updatedLimit = response.values.length > initialLimit ? response.values.length : initialLimit;
            setPage(1);
            setLimit(updatedLimit);
            setTableKey(prevKey => prevKey + 1);
            setHeadCellsConfig(headCells);
            setPagination(response.pagination);
            setAllRows(response.values);
            setRowData(response.values);

        } catch (error: any) {
            resetTable();
            appContext.notifyError(error.message);
        }
    }

    const resetTable = () => {
        setHeadCellsConfig([]);
        setRowData([]);
    };

    const handlePaginationChange = (page: number, limit: number) => {
        setPage(page);
        setLimit(limit);
    };

    const computeExportOption = () => {
        return {
            buttonLabel: 'EXPORT DETAILS TO EXCEL',
            onClick: downloadExcel,
        }
    };

    const downloadExcel = async () => {
        if (selectedIds.length === 0) {
            appContext.notifyWarning('Please select at least one row to export');

        } else {
            setDownloading(true);

            try {
                const response = await exportDataToExcel(authState!, selectedIds);

                // Check if the response is an ArrayBuffer
                if (response instanceof ArrayBuffer) {
                    const blob = new Blob([response], { type: 'application/octet-stream' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'setup_checkup.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } else {
                    appContext.notifyError("Error while validating data format");
                    console.error("Response is not an ArrayBuffer:", response);
                }
            } catch (error: any) {
                console.log("ERROR: ", error);
                appContext.notifyError(error.message);
            } finally {
                setDownloading(false);
            }
        }
    };

    const handleMassEdit = async (selectedIds: string[], fieldToEdit: string, newValueToApply: string, newValuesToApply: string[]) => {

    }

    const handleSelectAllClick = async () => {
        const ids = rowData
            .map(r => (typeof r.id === 'number' ? r.id : Number(r.id)))
            .filter((n): n is number => Number.isFinite(n)); // drop non-numeric IDs
        return ids;    }

    const handleSelectionChange = (ids: string[]) => {
        setSelectedIds(ids);
    };

    const applyFilters = (
        rows: RecordRepresentation[],
        headCells: HeadCell<RecordRepresentation>[]
    ) => {
        const active = headCells.filter(h => h.visible && (h.searchValue ?? '').trim() !== '');
        if (active.length === 0) return rows;

        return rows.filter(row =>
            active.every(hc => {
                const raw = row[hc.id];
                const text = String(raw ?? '').toLowerCase();
                const needle = String(hc.searchValue ?? '').trim().toLowerCase();

                // Optional: support multiple terms separated by space
                return needle
                    .split(/\s+/)
                    .every(term => term === '' || text.includes(term));
            })
        );
    }
    const handleMultiCellsSearch = ( newList: HeadCell<RecordRepresentation>[]) => {
        setPage(1);
        headCellsConfig.forEach((cell : HeadCell<RecordRepresentation>) => {
            cell.searchValue = newList.filter(newListCell => newListCell.id === cell.id)[0].searchValue;
        });
        console.log(headCellsConfig)
        const filtered = applyFilters(allRows, headCellsConfig);
        setRowData(filtered);
    };

    const handleSortChange = async (order: Order, orderBy: string) => {
        setOrder(order);
        setOrderBy(orderBy);
    };

    return (
        <Page title={wordingDiagnostic.title} descriptionLine1={wordingDiagnostic.description.line1}
              descriptionLine2={wordingDiagnostic.description.line2}>
            <DynamicDatatable
                key={tableKey}
                rowSelection={true}
                stickyHeader={true}
                deactiveSearch={false}
                deactiveOrder={true}
                headCells={headCellsConfig}
                allowExporting={computeExportOption()}
                isDownloading={downloading}
                data={rowData}
                initialOrderBy={'id'}
                showPadding={false}
                onSelectionChange={handleSelectionChange}
                rowSelectionOptions={{
                    onMassEdit: handleMassEdit,
                    onSelectAllClick: handleSelectAllClick,
                    toolbar: {
                        action: {
                            massEdit: wordingsDatatable.massEdit.toolbar.action.massEdit,
                            edit: wordingsDatatable.massEdit.toolbar.action.edit,
                            closeMassEdit: wordingsDatatable.massEdit.toolbar.action.closeMassEdit
                        }
                    },
                    dialog: {
                        title: wordingsDatatable.massExport.dialog.title,
                        warningMessage: wordingsDatatable.massExport.dialog.warningMessage,
                        confirmMessage: wordingsDatatable.massExport.dialog.confirmMessage,
                        cancelButtonLabel: wordingsDatatable.massExport.dialog.cancelButtonLabel,
                        applyButtonLabel: wordingsDatatable.massExport.dialog.applyButtonLabel,
                        cancelConfirmButtonLabel: wordingsDatatable.massExport.dialog.cancelConfirmButtonLabel,
                        applyConfirmButtonLabel: wordingsDatatable.massExport.dialog.applyConfirmButtonLabel
                    },
                    form: {
                        field: {
                            label: {
                                selectProperty: wordingsDatatable.massExport.form.field.label.selectProperty
                            }
                        }
                    }
                }}
                paginationOptions={(() => ({
                    page,
                    limit,
                    order,
                    orderBy,
                    total: pagination?.total || 0,
                    rowsPerPageValues: [
                        {nb: limit, isDefault: true},
                    ],
                    meetNbRowsPerPage: false,
                    rowsPerPageLabel: wordingsDatatable.pagination.rowsPerPage,
                    onPaginationChange: handlePaginationChange,
                    onHandleSortChange: handleSortChange,
                    onMultiCellsSearch: handleMultiCellsSearch
                }))()}
            />
        </Page>
    );

};
